"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "@/providers/auth-provider";
import { Lobby, LobbyPlayer, isUserInLobby } from "@/types/lobby";

interface LobbyContextType {
  activeLobby: Lobby | null;
  lobbyPlayers: LobbyPlayer[];
  isLoading: boolean;
  joinLobby: (lobbyId: string) => Promise<void>;
  leaveLobby: () => void;
  refreshLobby: () => Promise<void>;
  setActiveLobbyData: (lobby: Lobby | null) => void;
}

const LobbyContext = createContext<LobbyContextType | undefined>(undefined);

const POLL_INTERVAL = 10000; // 10 seconds

export function LobbyProvider({ children }: { children: React.ReactNode }) {
  const { user, getIdToken } = useAuth();
  const [activeLobby, setActiveLobby] = useState<Lobby | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Derived state: players in the lobby (excluding current user for display purposes)
  const lobbyPlayers =
    activeLobby?.players.filter(
      (p) => p.status === "confirmed" && p.userId !== user?.id
    ) || [];

  const fetchLobby = useCallback(
    async (lobbyId: string): Promise<Lobby | null> => {
      try {
        const response = await fetch(`/api/lobbies/${lobbyId}`);
        const data = await response.json();

        if (response.ok && data.lobby) {
          return data.lobby;
        }
        return null;
      } catch (error) {
        console.error("Error fetching lobby:", error);
        return null;
      }
    },
    []
  );

  const joinLobby = useCallback(
    async (lobbyId: string) => {
      if (!user) return;

      setIsLoading(true);
      try {
        const lobby = await fetchLobby(lobbyId);

        if (lobby && isUserInLobby(lobby, user.id)) {
          setActiveLobby(lobby);
          // Store in localStorage for persistence across page refreshes
          localStorage.setItem("activeLobbyId", lobbyId);
        }
      } catch (error) {
        console.error("Error joining lobby context:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [user, fetchLobby]
  );

  const leaveLobby = useCallback(() => {
    setActiveLobby(null);
    localStorage.removeItem("activeLobbyId");
  }, []);

  const refreshLobby = useCallback(async () => {
    if (!activeLobby || !user) return;

    const updatedLobby = await fetchLobby(activeLobby.id);

    if (updatedLobby) {
      // Check if user is still in the lobby
      if (isUserInLobby(updatedLobby, user.id)) {
        setActiveLobby(updatedLobby);
      } else {
        // User was kicked or lobby was cancelled
        leaveLobby();
      }
    } else {
      // Lobby no longer exists
      leaveLobby();
    }
  }, [activeLobby, user, fetchLobby, leaveLobby]);

  const setActiveLobbyData = useCallback((lobby: Lobby | null) => {
    setActiveLobby(lobby);
    if (lobby) {
      localStorage.setItem("activeLobbyId", lobby.id);
    } else {
      localStorage.removeItem("activeLobbyId");
    }
  }, []);

  // Restore lobby state from localStorage on mount
  useEffect(() => {
    if (user) {
      const storedLobbyId = localStorage.getItem("activeLobbyId");
      if (storedLobbyId) {
        joinLobby(storedLobbyId);
      }
    }
  }, [user, joinLobby]);

  // Clear lobby state when user logs out
  useEffect(() => {
    if (!user && activeLobby) {
      leaveLobby();
    }
  }, [user, activeLobby, leaveLobby]);

  // Poll for lobby updates when there's an active lobby
  useEffect(() => {
    if (activeLobby) {
      pollIntervalRef.current = setInterval(refreshLobby, POLL_INTERVAL);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [activeLobby, refreshLobby]);

  const value: LobbyContextType = {
    activeLobby,
    lobbyPlayers,
    isLoading,
    joinLobby,
    leaveLobby,
    refreshLobby,
    setActiveLobbyData,
  };

  return (
    <LobbyContext.Provider value={value}>{children}</LobbyContext.Provider>
  );
}

export function useLobby() {
  const context = useContext(LobbyContext);
  if (context === undefined) {
    throw new Error("useLobby must be used within a LobbyProvider");
  }
  return context;
}
